import React, { useState } from 'react';
import { Box } from '@mui/system';
import { useMutation } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import { AxiosError } from 'axios';
import { ReturnButton } from 'components';
import { createEmissionsAllocation, EmissionsAllocationDetails } from 'api';
import { useNotify } from 'hooks';
import { FormTitle, TitleInput, DateRangePicker, DimensionTitlesInput, SubmitButton } from './components';
import { validateFormData, getRequestBody } from './helpers';
import { FormData, FormErrors, IDataSetupFormProps } from '../../types';

export const DataSetupForm = ({ onBackToOverview, onFormSubmition }: IDataSetupFormProps) => {
    const [formData, setFormData] = useState<FormData>({
        title: '',
        viewFromDate: null,
        viewToDate: null,
        dimensionColumnTitles: '',
    });
    const [errors, setErrors] = useState<FormErrors>({
        title: '',
        viewFromDate: '',
        viewToDate: '',
    });

    const notify = useNotify();
    const { isLoading, mutateAsync: createEmissions } = useMutation<
        EmissionsAllocationDetails,
        AxiosError,
        { title: string; startDate: string; endDate: string; dimensions: string[] }
    >({
        mutationFn: createEmissionsAllocation,
        onSuccess: (data) => {
            notify.success('Emission Allocation successfully created.');
            onFormSubmition(data.allocationId);
        },
        onError: (error) => {
            notify.error(
                typeof error.response?.data === 'string' ? error.response?.data : 'Failed to create emissions allocation'
            );
        },
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleDateChange = (name: 'viewFromDate' | 'viewToDate') => (date: Dayjs | null) => {
        setFormData((prev) => ({ ...prev, [name]: date }));
        setErrors((prev) => {
            const newErrors = { ...prev, [name]: '' };
            if (name === 'viewToDate' && date && formData.viewFromDate && date < formData.viewFromDate) {
                newErrors.viewToDate = '"View To" date cannot be earlier than "View From" date';
            }
            return newErrors;
        });
    };

    const handleSubmit = async () => {
        const newErrors = validateFormData(formData);
        setErrors(newErrors);

        if (Object.values(newErrors).some((error) => error)) {
            return;
        }

        const requestBody = getRequestBody(formData);
        await createEmissions(requestBody);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }} aria-labelledby="emissions-allocation-form-title">
            <ReturnButton onClick={onBackToOverview} label="Return to Allocation Table" />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                <FormTitle title="Emissions Allocation Data Setup" />
                <TitleInput value={formData.title} onChange={handleInputChange} error={errors.title} />
                <DateRangePicker
                    viewFromDate={formData.viewFromDate}
                    viewToDate={formData.viewToDate}
                    onFromDateChange={handleDateChange('viewFromDate')}
                    onToDateChange={handleDateChange('viewToDate')}
                    fromError={errors.viewFromDate}
                    toError={errors.viewToDate}
                />
                <DimensionTitlesInput value={formData.dimensionColumnTitles} onChange={handleInputChange} />
                <SubmitButton isLoading={isLoading} onClick={handleSubmit} />
            </Box>
        </Box>
    );
};